// ── Alarm permission state (Android) ─────────────────────────────────────────
// Every OS permission an alarm depends on fails *silently*: the JS side still
// gets a resolved scheduleAll(), the item still shows an ⏰ Alarm badge, and
// nothing rings. This surfaces that state so the UI can say so out loud
// instead of letting someone trust an alarm that can never fire.
//
// See AlarmSchedulerPlugin.getPermissionState() for what each flag means and
// why it matters.
import { useCallback, useEffect, useState } from 'react';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

const AlarmScheduler = registerPlugin('AlarmScheduler');
const isNative = Capacitor.isNativePlatform();

// Everything true = nothing to warn about. Also the shape used on web, where
// none of this applies and the picker never offers Alarm mode anyway.
const ALL_GOOD = {
  exactAlarm: true,
  notifications: true,
  unrestrictedBattery: true,
};

export async function getAlarmPermissionState() {
  if (!isNative) return ALL_GOOD;
  try {
    return { ...ALL_GOOD, ...(await AlarmScheduler.getPermissionState()) };
  } catch {
    // An older build without the plugin method — don't invent a problem.
    return ALL_GOOD;
  }
}

// Resolves to null on success, or a message to show the user. Deliberately
// NOT silent: a swallowed failure here is indistinguishable from a dead
// button, which is exactly how the first version of this shipped.
export async function requestAlarmPermission(which) {
  if (!isNative) return "Only available in the Android app.";
  try {
    await AlarmScheduler.requestPermission({ which });
    return null;
  } catch (e) {
    return (e && (e.message || e.errorMessage)) || "Couldn't open that settings page.";
  }
}

// Only "Alarms & reminders" is warned about (Jade, 2026-10-07): without it
// Android refuses the exact alarm outright. Battery optimisation and
// notifications were dropped from the picker. getPermissionState() still
// reports all of them, unused here. The battery one used to promise a
// hand-off to the system Clock app that was removed long ago (995bff60).
const LABELS = {
  exactAlarm: {
    title: 'Alarms & reminders permission is off',
    body: 'Tap Fix, then switch on Alarms & reminders for Clarity.',
  },
};

export function alarmPermissionProblems(state) {
  if (!state) return [];
  return Object.keys(LABELS)
    .filter((k) => state[k] === false)
    .map((k) => ({ key: k, ...LABELS[k] }));
}

/**
 * Live permission state, re-checked whenever the app comes back to the
 * foreground — i.e. right after the user returns from the settings screen a
 * "Fix" button sent them to, so the warning clears itself.
 */
export function useAlarmPermissionState(enabled = true) {
  const [state, setState] = useState(ALL_GOOD);

  const refresh = useCallback(() => {
    if (!isNative || !enabled) return;
    getAlarmPermissionState().then(setState);
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!isNative || !enabled) return;
    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refresh();
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, [refresh, enabled]);

  return { state, problems: alarmPermissionProblems(state), refresh };
}
